"use client";

import { useCallback } from "react";
import { useAtom } from "jotai";
import Hotjar from "@hotjar/browser";
import { acceptedCookiePolicyAtom } from "~/atoms";
import { CookieTypeEnum } from "~/types-and-enums/cookieTypes";

/**
 * Check if Hotjar is initialized and ready
 */
const isHotjarReady = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    // Check if Hotjar is available and initialized
    const windowWithHotjar = window as typeof window & { hj?: unknown };
    return typeof Hotjar !== "undefined" && typeof windowWithHotjar.hj !== "undefined";
  } catch {
    return false;
  }
};

/**
 * Hook to interact with Hotjar API
 *
 * @example
 * ```tsx
 * const { identifyUser, triggerEvent, isEnabled } = useHotjar();
 *
 * // Identify user
 * identifyUser('user123', { email: 'user@example.com' });
 *
 * // Track custom event
 * triggerEvent('button_clicked', { button_name: 'submit' });
 * ```
 */
export const useHotjar = () => {
  const [acceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);

  const hasBehavioralConsent = acceptedCookiePolicy.includes(CookieTypeEnum.Behavioral);
  const hotjarSiteId = process.env.NEXT_PUBLIC_HOTJAR_SITE_ID;
  const isEnabled = hasBehavioralConsent && typeof window !== "undefined" && !!hotjarSiteId;

  /**
   * Identify a user in Hotjar
   * @param userId - Unique user identifier
   * @param attributes - Additional user attributes (email, name, etc.)
   */
  const identifyUser = useCallback(
    (userId: string, attributes?: Record<string, string | number | boolean>) => {
      if (!isEnabled) {
        return;
      }

      // Check if Hotjar is ready, if not, retry after a delay
      const attemptIdentify = (retries = 3, delay = 500) => {
        if (isHotjarReady()) {
          try {
            Hotjar.identify(userId, attributes || {});
          } catch (error) {
            console.error("Hotjar identify error:", error);
          }
        } else if (retries > 0) {
          // Retry after delay if Hotjar isn't ready yet
          setTimeout(() => attemptIdentify(retries - 1, delay), delay);
        }
      };

      attemptIdentify();
    },
    [isEnabled]
  );

  /**
   * Trigger a custom event in Hotjar
   * @param eventName - Name of the event
   */
  const triggerEvent = useCallback(
    (eventName: string) => {
      if (!isEnabled || !isHotjarReady()) {
        return;
      }

      try {
        Hotjar.event(eventName);
      } catch (error) {
        console.error("Hotjar event error:", error);
      }
    },
    [isEnabled]
  );

  /**
   * State change event (useful for SPA navigation)
   * @param relativePath - The relative path of the new state
   */
  const stateChange = useCallback(
    (relativePath: string) => {
      if (!isEnabled || !isHotjarReady()) {
        return;
      }

      try {
        Hotjar.stateChange(relativePath);
      } catch (error) {
        console.error("Hotjar stateChange error:", error);
      }
    },
    [isEnabled]
  );

  return {
    identifyUser,
    triggerEvent,
    stateChange,
    isEnabled: isEnabled && isHotjarReady(),
  };
};
